/**
 * Node Configuration Panel
 *
 * Side panel for configuring selected nodes
 */
import { useState, useEffect } from 'react'
import { clsx } from 'clsx'
import {
  XMarkIcon,
  Cog6ToothIcon,
  ServerIcon,
  CloudIcon,
  BoltIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline'
import { AgentNodeData, SPECIALIST_TYPES } from '@/types/architect'

interface NodeConfigPanelProps {
  nodeId: string
  data: AgentNodeData
  onUpdate: (nodeId: string, data: Partial<AgentNodeData>) => void
  onClose: () => void
  availableModels: string[]
}

export default function NodeConfigPanel({
  nodeId,
  data,
  onUpdate,
  onClose,
  availableModels,
}: NodeConfigPanelProps) {
  const [localData, setLocalData] = useState<AgentNodeData>(data)

  useEffect(() => {
    setLocalData(data)
  }, [data])

  const handleChange = (field: keyof AgentNodeData, value: any) => {
    const updated = { ...localData, [field]: value }
    setLocalData(updated)
    onUpdate(nodeId, { [field]: value })
  }

  const handleSave = () => {
    // Mark as configured if key fields are set
    const isConfigured = data.nodeType === 'input' || data.nodeType === 'output' ||
      (data.nodeType === 'specialist' && !!localData.specialistType) ||
      (data.nodeType === 'supervisor') ||
      (data.nodeType === 'router') ||
      (data.nodeType === 'aggregator' && !!localData.aggregationStrategy)

    onUpdate(nodeId, { ...localData, isConfigured })
    onClose()
  }

  return (
    <div className="w-72 bg-card border-l border-border flex flex-col h-full overflow-y-auto shrink-0">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Cog6ToothIcon className="w-5 h-5 text-primary-400" />
          <span className="font-medium text-foreground">Configure Node</span>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-muted-foreground hover:text-foreground rounded"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Label */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">
            Node Label
          </label>
          <input
            type="text"
            value={localData.label}
            onChange={(e) => handleChange('label', e.target.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Specialist Type */}
        {data.nodeType === 'specialist' && (
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Specialist Type
            </label>
            <select
              value={localData.specialistType || ''}
              onChange={(e) => handleChange('specialistType', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select type...</option>
              {SPECIALIST_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Provider Mode */}
        {(data.nodeType === 'specialist' || data.nodeType === 'supervisor') && (
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Provider Mode
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { mode: 'local', icon: ServerIcon, color: 'green' },
                { mode: 'api', icon: CloudIcon, color: 'blue' },
                { mode: 'auto', icon: BoltIcon, color: 'yellow' },
                { mode: 'hybrid', icon: ArrowsRightLeftIcon, color: 'purple' },
              ].map(({ mode, icon: Icon, color }) => (
                <button
                  key={mode}
                  onClick={() => handleChange('providerMode', mode)}
                  className={clsx(
                    'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors',
                    localData.providerMode === mode
                      ? `bg-${color}-900/30 border-${color}-600 text-${color}-400`
                      : 'bg-background border-border text-muted-foreground hover:border-border'
                  )}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm capitalize">{mode}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Local Model */}
        {localData.providerMode === 'local' || localData.providerMode === 'hybrid' ? (
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Local Model
            </label>
            <select
              value={localData.localModel || ''}
              onChange={(e) => handleChange('localModel', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Auto-select</option>
              {availableModels.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {/* Execution Mode */}
        {(data.nodeType === 'specialist' || data.nodeType === 'supervisor') && (
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Execution Mode
            </label>
            <div className="space-y-2">
              {[
                { mode: 'direct', desc: 'Single pass, no review' },
                { mode: 'simple', desc: 'Basic quality check' },
                { mode: 'triad', desc: 'Full quality review cycle' },
                { mode: 'full', desc: 'Multi-specialist with aggregation' },
              ].map(({ mode, desc }) => (
                <label
                  key={mode}
                  className={clsx(
                    'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                    localData.executionMode === mode
                      ? 'bg-primary-900/30 border-primary-600'
                      : 'bg-background border-border hover:border-border'
                  )}
                >
                  <input
                    type="radio"
                    name="executionMode"
                    checked={localData.executionMode === mode}
                    onChange={() => handleChange('executionMode', mode)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-foreground capitalize">{mode}</div>
                    <div className="text-xs text-muted-foreground">{desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Aggregation Strategy */}
        {data.nodeType === 'aggregator' && (
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Aggregation Strategy
            </label>
            <div className="space-y-2">
              {[
                { strategy: 'concatenate', desc: 'Combine all outputs sequentially' },
                { strategy: 'summarize', desc: 'AI-generated summary of all outputs' },
                { strategy: 'vote', desc: 'Select most common answer' },
                { strategy: 'best', desc: 'Select highest quality output' },
              ].map(({ strategy, desc }) => (
                <label
                  key={strategy}
                  className={clsx(
                    'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                    localData.aggregationStrategy === strategy
                      ? 'bg-blue-900/30 border-blue-600'
                      : 'bg-background border-border hover:border-border'
                  )}
                >
                  <input
                    type="radio"
                    name="aggregationStrategy"
                    checked={localData.aggregationStrategy === strategy}
                    onChange={() => handleChange('aggregationStrategy', strategy)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-foreground capitalize">{strategy}</div>
                    <div className="text-xs text-muted-foreground">{desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Quality Settings */}
        {(data.nodeType === 'specialist' || data.nodeType === 'supervisor') && (
          <div>
            <label className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                checked={localData.enableQualityReview || false}
                onChange={(e) => handleChange('enableQualityReview', e.target.checked)}
                className="rounded border-border text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-muted-foreground">Enable Quality Review</span>
            </label>

            {localData.enableQualityReview && (
              <div className="space-y-4 pl-6">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">
                    Quality Threshold (1-10)
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    step="0.5"
                    value={localData.qualityThreshold || 7}
                    onChange={(e) => handleChange('qualityThreshold', parseFloat(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-xs text-muted-foreground text-right">
                    {localData.qualityThreshold || 7}
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-muted-foreground mb-1">
                    Max Revisions
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={localData.maxRevisions || 2}
                    onChange={(e) => handleChange('maxRevisions', parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-border">
        <button
          onClick={handleSave}
          className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-foreground rounded-lg font-medium transition-colors"
        >
          Save Configuration
        </button>
      </div>
    </div>
  )
}
